export const TERM_EXPLANATIONS = {
  GitHub: '公开存放代码和项目作品的网站，可以理解成程序员的作品集平台。',
  API: '软件之间互相交换数据的接口，比如网页向后端要一份用户信息。',
  数据库: '专门保存数据的地方，用户、订单、成绩、文章都可以存在里面。',
  前端: '用户直接看到和点击的页面部分，比如网页、小程序界面和按钮。',
  后端: '在页面背后处理登录、保存数据、业务规则的服务部分。',
  Docker: '把项目运行环境打包起来的工具，方便在不同电脑或服务器上稳定运行。',
  Redis: '让网站访问更快的缓存工具，常用来保存验证码、登录状态或热门数据。',
  'Spring Boot': 'Java 做网站后端的常用框架，能更快搭出登录、接口和管理系统。',
  MySQL: '常见关系型数据库，适合保存用户、订单、文章、成绩这类结构化数据。',
  'AI Agent': '能按目标分步骤调用工具、整理信息和完成任务的 AI 助手。',
  开源项目: '公开给别人学习、使用或二次开发的项目，适合拿来参考思路和练习。',
}

const TERM_KEYS = Object.keys(TERM_EXPLANATIONS)

export function findTermExplanations(values, limit = 4) {
  const text = values.flat().filter(Boolean).join(' ')
  return TERM_KEYS
    .filter((term) => text.toLowerCase().includes(term.toLowerCase()))
    .slice(0, limit)
    .map((term) => ({ term, explanation: TERM_EXPLANATIONS[term] }))
}

export function beginnerTags({ advanced = false, resume = true, learning = true } = {}) {
  return [
    advanced ? '适合进阶' : '适合0基础',
    resume ? '可写简历' : '',
    learning ? '推荐学习' : '',
  ].filter(Boolean)
}

export function buildFriendlyToolDetails(tool = {}) {
  const name = String(tool.name || '')
  const scene = String(tool.scene || '')
  const text = `${name} ${scene}`

  if (/GitHub|开源/i.test(text)) {
    return {
      what: '帮你把开源项目看懂，提炼它能学什么、亮点在哪里、能不能写进简历。',
      who: '适合想做项目但不知道参考什么的同学。',
      why: '能减少筛项目的时间，把“看不懂的仓库”变成学习路线和简历素材。',
      start: '0基础先不用读源码，先看项目解决了什么问题、页面长什么样、用了哪些工具。',
      resume: '可以。学会后能写“分析并复现开源项目，整理技术亮点和改进方案”。',
      careers: '软件开发、AI 应用、测试、技术支持、产品助理',
      rating: '★★★★★',
      next: '下一步：找一个小项目，先跑起来，再改一个小功能。',
      tags: beginnerTags(),
    }
  }

  if (/简历|Resume/i.test(text)) {
    return {
      what: '帮你检查简历哪里不清楚，把经历改成 HR 和面试官更容易看懂的表达。',
      who: '适合准备实习、校招或转专业求职的学生。',
      why: '很多学生不是没经历，而是不会把课程、比赛和项目写成求职语言。',
      start: '0基础先上传一版现有简历；没有简历也可以先整理项目、比赛和校园经历。',
      resume: '可以。它本身就是为了把经历改成能投递的简历内容。',
      careers: '所有实习、校招、转专业求职方向',
      rating: '★★★★★',
      next: '下一步：先优化一段项目经历，再匹配目标岗位。',
      tags: beginnerTags(),
    }
  }

  if (/项目|Project/i.test(text)) {
    return {
      what: '把“我该做什么项目”拆成选题、功能、步骤、材料和简历表达。',
      who: '适合简历缺项目、课程设计不知道做什么、作品集空白的同学。',
      why: '项目比单纯看教程更容易沉淀成截图、演示、答辩和简历内容。',
      start: '0基础先选一个小而完整的项目，不要一开始做复杂平台。',
      resume: '可以。只要能演示、能截图、能讲清楚，就适合写进简历。',
      careers: '开发、产品、运营、设计、数据分析、AI 应用',
      rating: '★★★★★',
      next: '下一步：确定一个最小版本，先完成核心流程。',
      tags: beginnerTags(),
    }
  }

  if (/比赛|竞赛|Competition/i.test(text)) {
    return {
      what: '帮你从专业、年级和目标出发，选择更值得投入的比赛方向。',
      who: '适合想补经历、拿奖、做项目但不知道选什么比赛的同学。',
      why: '比赛能补简历、综测和项目经历，但选错会浪费很多时间。',
      start: '0基础先选门槛较低、能产出作品或报告的比赛。',
      resume: '可以。有作品、奖项、调研报告或路演材料时更适合写。',
      careers: '产品、运营、开发、数据分析、设计、创新创业方向',
      rating: '★★★★☆',
      next: '下一步：先看比赛时间、交付物和是否适合当前年级。',
      tags: beginnerTags({ resume: true }),
    }
  }

  if (/写作|论文|报告|PPT|Reading|Paper|Writing/i.test(text)) {
    return {
      what: '帮你整理文章、报告、PPT 或答辩内容结构，不是替你抄一篇。',
      who: '适合材料很多但不知道怎么组织观点、证据和展示顺序的同学。',
      why: '能把零散材料变成大纲，降低写报告和做展示的启动难度。',
      start: '0基础先列出老师要求、字数、主题和必须包含的部分。',
      resume: '间接有帮助。可以提升报告、答辩和作品集表达质量。',
      careers: '文职、运营、教育、产品、市场、设计展示方向',
      rating: '★★★★☆',
      next: '下一步：先生成大纲，再自己补真实内容和案例。',
      tags: beginnerTags({ resume: false }),
    }
  }

  return {
    what: '帮你把学习或任务拆成更清楚的小步骤。',
    who: '适合不知道从哪里开始、需要先整理思路的学生。',
    why: '能把模糊目标变成今天能完成的一步。',
    start: '0基础先输入目标和当前水平，让系统给出第一步。',
    resume: '看产出而定。能形成作品、报告或项目记录时更适合写进简历。',
    careers: '学习提升、实习准备、项目实践相关方向',
    rating: '★★★★☆',
    next: '下一步：先完成一个 30 分钟内能做完的小任务。',
    tags: beginnerTags({ resume: false }),
  }
}

export function buildFriendlyProjectDetails(project = {}, target = '') {
  const title = String(project?.title?.zh || project?.title || '')
  const audience = String(project?.audience?.zh || project?.audience || '')
  const techStack = Array.isArray(project?.techStack?.zh)
    ? project.techStack.zh
    : Array.isArray(project?.techStack)
      ? project.techStack
      : []
  const stackText = techStack.join(' ')
  const fullText = `${title} ${audience} ${stackText}`
  const isBackend = /Java|Spring|Redis|MySQL|后端|Docker/i.test(fullText)
  const isAi = /AI|Agent|大模型|Prompt/i.test(fullText)
  const isData = /数据|SQL|Excel|Python|分析/i.test(fullText)
  const advanced = /进阶|高级|高并发|秒杀/.test(`${project?.level?.zh || project?.level || ''} ${title}`)

  const careers = isBackend
    ? 'Java 后端、后端开发、测试、运维 / 云服务'
    : isAi
    ? 'AI 应用开发、产品助理、数据分析、软件开发'
    : isData
      ? '数据分析、运营分析、商业分析、产品助理'
      : '产品、运营、前端、软件实施、技术支持'

  return {
    what: isBackend
      ? '练习网站后台能力，重点是登录、保存数据、接口和业务流程。'
      : isData
        ? '练习把数据整理成结论，重点是清洗数据、做图表和解释发现。'
        : isAi
          ? '练习把 AI 用到真实场景，重点是输入、提示词、结果整理和边界说明。'
          : '练习做一个能展示的作品，重点是完成真实流程并留下截图和说明。',
    practice: isBackend
      ? '能练接口设计、数据库读写、缓存和后台管理。'
      : isData
        ? '能练数据清洗、指标分析、图表表达和报告总结。'
        : isAi
          ? '能练场景拆解、AI 工具使用、结果评估和产品表达。'
          : '能练需求拆解、流程设计、材料整理和展示表达。',
    fit: advanced
      ? '不建议完全0基础直接做完整版，先做简化版核心流程。'
      : '适合当前阶段先做最小可运行版本，再逐步加功能。',
    why: target
      ? `你的目标偏向“${target}”，这个项目能补一段可展示、可讲清楚的经历。`
      : '它比纯看教程更适合新手，因为最后能产出页面、截图、报告或简历材料。',
    start: isBackend
      ? '0基础先理解“页面提交数据 -> 后端处理 -> 数据库保存 -> 页面显示结果”。'
      : isData
        ? '0基础先找一份小数据，完成导入、整理、画图、写结论四步。'
        : isAi
          ? '0基础先做一个固定场景的小助手，不要一开始追求全自动。'
          : '0基础先写清用户是谁、要完成哪 3 个动作、最终交付什么。',
    resume: '可以写进简历，前提是你真的做过、能演示、能讲清自己负责的部分。',
    careers,
    rating: advanced ? '★★★★☆' : '★★★★★',
    next: '下一步：先画出核心流程，今天只完成第一个可运行小功能。',
    tags: beginnerTags({ advanced, resume: true }),
  }
}

export function explainRoadmapItem(item = '', target = '') {
  const details = buildFriendlyProjectDetails({ title: item, audience: target, techStack: [item] }, target)
  return {
    title: item,
    ...details,
  }
}
